// Thin typed fetch wrapper for the FastAPI backend.
// - Always sends credentials (session cookie).
// - On POST/PUT/PATCH/DELETE, sends X-CSRF-Token from the vc_csrf cookie.
// - Parses JSON; throws ApiError on non-2xx.
//
// Server components should use this via absolute URL through API_BASE_URL.
// Client components hit the same path; Next.js rewrites proxy /api to FastAPI.

import { CSRF_HEADER, readCsrfTokenFromCookie } from "./csrf";
import type {
  ApiError,
  EntityCountsByKind,
  EntityDetail,
  EntityListResponse,
  EntitySummary,
  RenderPreviewRequest,
  RenderPreviewResponse,
  WhoamiResponse,
  WikilinkSuggestion,
} from "./types";

export class ApiClientError extends Error implements ApiError {
  status: number;
  code: string;
  detail?: unknown;
  constructor(status: number, code: string, message: string, detail?: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface RequestOpts {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
  // For server-side use only: pass an explicit Cookie header.
  cookie?: string;
  // For server-side requests that should bypass Next rewrite logic.
  baseUrl?: string;
  // RSC fetch caching hint (Next.js extension).
  next?: { revalidate?: number; tags?: string[] };
  // BCP-47 locale tag for the ``X-Locale`` header. When unset and the
  // call originates on the client, the locale is auto-derived from the
  // URL prefix; server-side callers should pass it explicitly so the
  // header reaches FastAPI on RSC fetches.
  locale?: string;
}

function isMutation(method: Method): boolean {
  return method !== "GET";
}

/** Read the active locale from the URL (client) or return empty string
 *  (server). Server callers should pass ``locale`` via opts; this helper
 *  is a client-only fallback so every fetch from a ``"use client"``
 *  surface picks up the operator's current locale without each caller
 *  having to wire it through. */
function getClientLocale(): string {
  if (typeof window === "undefined") return "";
  const seg = window.location.pathname.split("/")[1] ?? "";
  // Match the literal locales declared in ``i18n/routing.ts``. Keep this
  // list in sync; the cost of drift is the X-Locale header silently
  // dropping out.
  if (seg === "en" || seg === "zh-CN") return seg;
  return "";
}

/** Connection-level failures where the request never got a response. */
const _STALE_SOCKET_CODES = new Set(["UND_ERR_SOCKET", "ECONNRESET", "EPIPE"]);

function isStaleSocketError(e: unknown): boolean {
  const cause = (e as { cause?: { code?: unknown } } | null)?.cause;
  return typeof cause?.code === "string" && _STALE_SOCKET_CODES.has(cause.code);
}

/**
 * Retry a server-side GET once when the connection died underneath us.
 *
 * Node's undici keeps HTTP/1.1 sockets pooled and idles them out at ~4 s,
 * while uvicorn closes idle connections at its own keep-alive deadline. When
 * this box is under CPU contention the client's idle timer fires late, so
 * undici hands out a socket the server has already closed and the request
 * dies with ``UND_ERR_SOCKET: other side closed`` — surfacing in RSC as a bare
 * ``TypeError: fetch failed``. The socket is discarded on error, so a single
 * immediate retry gets a fresh connection.
 *
 * Only idempotent methods are retried: the request bytes were already written,
 * so replaying a mutation could apply it twice. ``scripts/dev_api.sh`` also
 * raises uvicorn's ``--timeout-keep-alive`` above the client window, which
 * removes the race at the source; this is the belt to that braces, and it
 * covers every RSC call rather than just the ``whoami`` ladder in ``lib/auth``.
 */
async function fetchWithStaleSocketRetry(
  url: string,
  init: RequestInit,
  method: string,
): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    const idempotent = method === "GET" || method === "HEAD";
    if (!idempotent || typeof window !== "undefined" || !isStaleSocketError(e)) throw e;
    return await fetch(url, init);
  }
}

async function request<T>(path: string, opts: RequestOpts = {}): Promise<T> {
  const method = opts.method ?? "GET";
  const base = opts.baseUrl ?? (typeof window === "undefined" ? serverBaseUrl() : "");
  const url = `${base}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.cookie) {
    headers.Cookie = opts.cookie;
  }
  if (isMutation(method)) {
    const csrf = readCsrfTokenFromCookie(opts.cookie);
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  // X-Locale propagation — the API proxy at app/api/[...path]/route.ts
  // forwards this header to FastAPI, which uses it to default workflow
  // ``lang`` payloads when not explicitly set. See Phase 17 i18n plan
  // for the precedence rules.
  const locale = opts.locale ?? getClientLocale();
  if (locale) headers["X-Locale"] = locale;

  const init: RequestInit = {
    method,
    headers,
    credentials: "include",
  };
  if (opts.body !== undefined) {
    init.body = JSON.stringify(opts.body);
  }
  if (opts.signal) init.signal = opts.signal;
  // Pass Next.js cache hints through; ignored in non-Next runtimes.
  if (opts.next) {
    (init as RequestInit & { next?: RequestOpts["next"] }).next = opts.next;
  }

  const res = await fetchWithStaleSocketRetry(url, init, method);
  const text = await res.text();
  const json = text ? safeParse(text) : null;

  if (!res.ok) {
    const code =
      (json && typeof json === "object" && "code" in json && typeof json.code === "string"
        ? json.code
        : null) ?? `HTTP_${res.status}`;
    const message =
      (json && typeof json === "object" && "message" in json && typeof json.message === "string"
        ? json.message
        : null) ?? res.statusText;
    throw new ApiClientError(res.status, code, message, json);
  }

  return json as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function serverBaseUrl(): string {
  // 127.0.0.1, not `localhost`: Node undici resolves `localhost` to ::1 first;
  // uvicorn binds IPv4 only, so the IPv6 attempt ECONNREFUSEDs as `fetch failed`.
  return process.env.API_BASE_URL ?? "http://127.0.0.1:8000";
}

// ────────────────────────────────────────────────────────────────────────────
// Public typed surface. Server actions and route handlers pass cookies when
// calling from RSC; client components rely on the browser's cookie jar.
// ────────────────────────────────────────────────────────────────────────────

export const api = {
  whoami(opts?: RequestOpts): Promise<WhoamiResponse> {
    return request<WhoamiResponse>("/api/auth/whoami", opts);
  },

  logoutAll(opts?: RequestOpts): Promise<{ ok: true }> {
    return request<{ ok: true }>("/api/auth/logout-all", { ...opts, method: "POST" });
  },

  /** Shallow-merge a patch into the current user's metadata bag. The
   *  backend uses ``null`` values to mean delete-this-key. Used by
   *  ``LocalePicker`` to persist ``preferred_language`` so a fresh
   *  login lands on the same locale even from a different browser. */
  patchMetadata(
    patch: Record<string, unknown>,
    opts?: RequestOpts,
  ): Promise<{ user_id: string; metadata: Record<string, unknown> }> {
    return request<{ user_id: string; metadata: Record<string, unknown> }>(
      "/api/users/me/metadata",
      { ...opts, method: "PATCH", body: { patch } },
    );
  },

  entities: {
    list(
      params: {
        search?: string;
        kind?: string;
        sensitivity?: string;
        page?: number;
        page_size?: number;
      } = {},
      opts?: RequestOpts,
    ): Promise<EntityListResponse> {
      const qs = new URLSearchParams();
      if (params.search) qs.set("search", params.search);
      if (params.kind) qs.set("kind", params.kind);
      if (params.sensitivity) qs.set("sensitivity", params.sensitivity);
      if (params.page) qs.set("page", String(params.page));
      if (params.page_size) qs.set("page_size", String(params.page_size));
      const tail = qs.toString();
      return request<EntityListResponse>(`/api/entities${tail ? `?${tail}` : ""}`, opts);
    },
    getBySlug(kind: string, slug: string, opts?: RequestOpts): Promise<EntityDetail> {
      return request<EntityDetail>(
        `/api/entities/by-slug/${encodeURIComponent(kind)}/${encodeURIComponent(slug)}`,
        opts,
      );
    },
    get(id: string, opts?: RequestOpts): Promise<EntityDetail> {
      return request<EntityDetail>(`/api/entities/${encodeURIComponent(id)}`, opts);
    },
    create(input: unknown, opts?: RequestOpts): Promise<EntitySummary> {
      // Translate frontend shape (`name`, `tags`) into the API's EntityCreate
      // shape (`display_name`, `metadata.tags`). The API has
      // `additionalProperties: false`, so untranslated fields → 422.
      const src = (input ?? {}) as Record<string, unknown>;
      const body: Record<string, unknown> = {};
      for (const k of [
        "kind",
        "slug",
        "sensitivity",
        "aliases",
        "summary",
        "frontmatter",
        "body_md",
      ]) {
        if (k in src && src[k] !== undefined) body[k] = src[k];
      }
      if (typeof src.name === "string" && !("display_name" in src)) {
        body.display_name = src.name;
      } else if (typeof src.display_name === "string") {
        body.display_name = src.display_name;
      }
      const tags = Array.isArray(src.tags) ? src.tags : null;
      const incomingMeta =
        src.metadata && typeof src.metadata === "object"
          ? (src.metadata as Record<string, unknown>)
          : {};
      if (tags && tags.length > 0) {
        body.metadata = { ...incomingMeta, tags };
      } else if (Object.keys(incomingMeta).length > 0) {
        body.metadata = incomingMeta;
      }
      return request<EntitySummary>("/api/entities", { ...opts, method: "POST", body });
    },
    remove(
      id: string,
      params: { hard?: boolean } = {},
      opts?: RequestOpts,
    ): Promise<void> {
      const qs = params.hard ? "?hard=true" : "";
      return request<void>(`/api/entities/${encodeURIComponent(id)}${qs}`, {
        ...opts,
        method: "DELETE",
      });
    },
    update(id: string, patch: unknown, opts?: RequestOpts): Promise<EntityDetail> {
      // Translate frontend ``name`` → API ``display_name`` (mirrors
      // ``create`` above). The API's ``EntityPatch`` has
      // ``extra="forbid"``, so a stray ``name`` field 422s the whole
      // save — which is exactly what tripped the editor on 2026-06-15.
      const src = (patch ?? {}) as Record<string, unknown>;
      const body: Record<string, unknown> = { ...src };
      if (typeof body.name === "string" && !("display_name" in body)) {
        body.display_name = body.name;
        delete body.name;
      }
      return request<EntityDetail>(`/api/entities/${encodeURIComponent(id)}`, {
        ...opts,
        method: "PATCH",
        body,
      });
    },
    counts(opts?: RequestOpts): Promise<EntityCountsByKind> {
      return request<EntityCountsByKind>("/api/entities/counts", opts);
    },
    suggest(prefix: string, opts?: RequestOpts): Promise<WikilinkSuggestion[]> {
      const qs = new URLSearchParams({ search: prefix });
      return request<WikilinkSuggestion[]>(`/api/entities?${qs.toString()}`, opts).then((resp) => {
        // Accept either a list or a list response; coerce if upstream returns
        // the paginated shape.
        const r = resp as unknown;
        if (Array.isArray(r)) return r as WikilinkSuggestion[];
        if (
          r &&
          typeof r === "object" &&
          "items" in r &&
          Array.isArray((r as { items: unknown }).items)
        ) {
          return (r as { items: WikilinkSuggestion[] }).items;
        }
        return [];
      });
    },
  },

  render: {
    preview(req: RenderPreviewRequest, opts?: RequestOpts): Promise<RenderPreviewResponse> {
      return request<RenderPreviewResponse>("/api/render/preview", {
        ...opts,
        method: "POST",
        body: req,
      });
    },
  },

  meetings: {
    // The PDF upload path uses raw ``fetch`` from the
    // ImportMeetingPdfButton — it has to control Content-Type for
    // multipart. This wrapper covers the JSON-body distill endpoint.
    distill(
      meetingId: string,
      opts?: RequestOpts & { force?: boolean },
    ): Promise<{
      meeting_id: string;
      skipped: boolean;
      reason: string | null;
      summary: string | null;
      updated_entities: string[];
    }> {
      const { force, ...rest } = opts ?? {};
      return request(`/api/meetings/${encodeURIComponent(meetingId)}/distill`, {
        ...rest,
        method: "POST",
        body: { force: force ?? false },
      });
    },
  },
};

export type Api = typeof api;
